import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { CookieConsent } from "@/components/CookieConsent";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";

import { Landing } from "@/pages/Landing";
import { Login, Register, ForgotPassword, ResetPassword } from "@/pages/Auth";
import { AuthCallback } from "@/pages/AuthCallback";
import { Onboarding } from "@/pages/Onboarding";
import { Dashboard } from "@/pages/Dashboard";
import { Analytics } from "@/pages/Analytics";
import { Reels } from "@/pages/Reels";
import { Insights } from "@/pages/Insights";
import { ContentAI } from "@/pages/ContentAI";
import { Calendar } from "@/pages/Calendar";
import { Pricing } from "@/pages/Pricing";
import { Account } from "@/pages/Account";
import { Billing } from "@/pages/Billing";
import { Admin } from "@/pages/Admin";
import { Legal } from "@/pages/Legal";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";

const FullLoader = () => (
  <div className="flex min-h-screen flex-col items-center justify-center gap-4">
    <Logo />
    <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
  </div>
);

function Protected({ children, adminOnly }) {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && !user.is_admin) return <Navigate to="/dashboard" replace />;
  if (!user.onboarding_completed) return <Navigate to="/onboarding" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/privacy" element={<Legal kind="privacy" />} />
      <Route path="/terms" element={<Legal kind="terms" />} />
      <Route path="/cookies" element={<Legal kind="cookies" />} />
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/analytics" element={<Protected><Analytics /></Protected>} />
      <Route path="/reels" element={<Protected><Reels /></Protected>} />
      <Route path="/insights" element={<Protected><Insights /></Protected>} />
      <Route path="/content-ai" element={<Protected><ContentAI /></Protected>} />
      <Route path="/calendar" element={<Protected><Calendar /></Protected>} />
      <Route path="/account" element={<Protected><Account /></Protected>} />
      <Route path="/billing" element={<Protected><Billing /></Protected>} />
      <Route path="/admin" element={<Protected adminOnly><Admin /></Protected>} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
      <CookieConsent />
      <Toaster position="top-center" richColors />
    </BrowserRouter>
  );
}

export default App;
